'use client';

import classNames from 'classnames';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import MapOverlay from '@alltrails/amplitude/enums/MapOverlay';
import logExploreViewSwitched from '@alltrails/amplitude/events/logExploreViewSwitched';
import Button from '@alltrails/denali/components/Button';
import List from '@alltrails/denali/icons/List';
import MapFilled from '@alltrails/denali/icons/MapFilled';
import RunningTrail from '@alltrails/denali/icons/RunningTrail';
import useMap from '@alltrails/maps/hooks/useMap';
import { getMapBaseLayer, getMapOverlay } from '@alltrails/maps/utils/amplitudeHelpers';
import useIsPage from '@/hooks/useIsPage';
import { getFilterNames } from '@/utils/analyticsHelpers';
import { useAppDispatch, useAppSelector } from '@/utils/redux';
import { setIsShowingMobileList } from '@/utils/slices/exploreSlice';
import styles from './MobileViewToggle.module.scss';

const MobileViewToggle = ({ className }: { className?: string }) => {
  const map = useMap();
  const { isExploreTrail, isExploreRecording, isTripPlanner, isSavedCustomRoute } = useIsPage();
  const dispatch = useAppDispatch();
  const {
    baseStyleId,
    distanceAwayIsochrone,
    dynamicStyleIds,
    exploreServiceFilters,
    exploreViewType,
    isShowingMobileList,
    renderedPOIs,
    searchResultsSummary,
    staticStyleIds,
    trailEngagement,
    tripPlannerHasItinerary
  } = useAppSelector(state => ({
    baseStyleId: state.map.baseStyleId,
    distanceAwayIsochrone: state.exploreService.distanceAwayIsochrone,
    dynamicStyleIds: state.map.dynamicStyleIds,
    exploreServiceFilters: state.exploreService.filters,
    exploreViewType: state.explore.exploreViewType,
    isShowingMobileList: state.explore.isShowingMobileList,
    renderedPOIs: state.map.renderedPOIs,
    searchResultsSummary: state.exploreService.searchResultsSummary,
    staticStyleIds: state.map.staticStyleIds,
    trailEngagement: state.exploreService.trailEngagement,
    tripPlannerHasItinerary: state.tripPlanner.status === 'success' && Boolean(state.tripPlanner.result)
  }));

  const onClick = useCallback(() => {
    logExploreViewSwitched({
      bearing: map?.getBearing(),
      coordinates: map ? [map?.getCenter().lng, map?.getCenter().lat] : undefined,
      explore_view_type: exploreViewType ?? 'map',
      filters_applied: getFilterNames(exploreServiceFilters, Boolean(distanceAwayIsochrone), Boolean(trailEngagement)),
      map_base_layer: getMapBaseLayer(baseStyleId),
      map_overlays: [...dynamicStyleIds, ...staticStyleIds].map(id => getMapOverlay(id) as MapOverlay),
      method: 'click',
      new_state: isShowingMobileList ? 'map' : 'list',
      num_pois: renderedPOIs?.count,
      num_results_returned: searchResultsSummary?.count,
      pitch: map?.getPitch(),
      previous_state: isShowingMobileList ? 'list' : 'map',
      zoom_level: map?.getZoom()
    });
    dispatch(setIsShowingMobileList(!isShowingMobileList));
  }, [
    baseStyleId,
    dispatch,
    distanceAwayIsochrone,
    dynamicStyleIds,
    exploreServiceFilters,
    exploreViewType,
    isShowingMobileList,
    map,
    renderedPOIs,
    searchResultsSummary?.count,
    staticStyleIds,
    trailEngagement
  ]);

  // The saved-route detail view has its own map toggle in the action bar, so skip this floating pill there to avoid overlap.
  if (isShowingMobileList && isSavedCustomRoute) {
    return null;
  }

  // The trip planner has nothing to place on the map until an itinerary is generated, so hide the toggle while the
  // intake form is showing. When the map is showing (e.g. after a desktop -> mobile resize forces the list closed),
  // keep the toggle so the user still has a way back to the form.
  if (isTripPlanner && !tripPlannerHasItinerary && isShowingMobileList) {
    return null;
  }

  return (
    <Button
      className={classNames(styles.mobileViewToggle, className)}
      icon={{ Component: isShowingMobileList ? MapFilled : isExploreRecording ? RunningTrail : isSavedCustomRoute ? RunningTrail : List }}
      onClick={onClick}
      size="sm"
      text={
        isShowingMobileList ? (
          <FormattedMessage defaultMessage="Map" />
        ) : isExploreTrail ? (
          <FormattedMessage defaultMessage="Trail" />
        ) : isExploreRecording ? (
          <FormattedMessage defaultMessage="Activity" />
        ) : isSavedCustomRoute ? (
          <FormattedMessage defaultMessage="Custom route" />
        ) : (
          <FormattedMessage defaultMessage="List" />
        )
      }
      variant="accent"
    />
  );
};

export default MobileViewToggle;
